import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  createOrgCustomRoleApiv2,
  deleteOrgCustomRoleApi,
  listOrgCustomRolesApiv2,
  updateOrgCustomRoleApiv2,
  type CreateOrgCustomRoleRequestContent,
  type UpdateOrgCustomRoleRequestContent,
  type ListOrgCustomRolesResponseContent
} from '../../org-roles';

/**
 * TODO: CDK - Improve error responses across all custom role endpoints
 * Many endpoints return generic 500 "Internal Server Error" instead of specific validation errors.
 * Should return 400/403/404 with descriptive messages for common issues like:
 * - Role not found
 * - Duplicate role name
 * - Invalid permissions (return the list of valid OrgPermission values)
 * - Cannot delete role in use by members
 *
 * Valid OrgPermission values (must be exact lowercase with underscores):
 * - manage_members
 * - manage_billing
 * - manage_settings
 * - view_all_contracts
 * - manage_contracts
 * - invite_users
 * - manage_roles
 * - view_analytics
 */

/**
 * Grouped test component for all Custom Role endpoints
 */
const TestRoles: React.FC = () => {
  // Create Role state
  const [createOrgId, setCreateOrgId] = useState('');
  const [createRoleName, setCreateRoleName] = useState('');
  const [createRoleDescription, setCreateRoleDescription] = useState('');
  const [createPermissions, setCreatePermissions] = useState('');
  const [createResponse, setCreateResponse] = useState<any>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // List Roles state
  const [listOrgId, setListOrgId] = useState('');
  const [listResponse, setListResponse] = useState<ListOrgCustomRolesResponseContent | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Update Role state
  const [updateOrgId, setUpdateOrgId] = useState('');
  const [updateRoleId, setUpdateRoleId] = useState('');
  const [updateRoleName, setUpdateRoleName] = useState('');
  const [updatePermissions, setUpdatePermissions] = useState('');
  const [updateResponse, setUpdateResponse] = useState<any>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Delete Role state
  const [deleteOrgId, setDeleteOrgId] = useState('');
  const [deleteRoleId, setDeleteRoleId] = useState('');
  const [deleteResponse, setDeleteResponse] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Handlers
  const handleCreateRole = async () => {
    if (!createOrgId.trim() || !createRoleName.trim() || !createPermissions.trim()) {
      setCreateError('Please enter organization ID, role name, and permissions');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    try {
      // Parse permissions from comma-separated string
      // Valid values: manage_members, manage_billing, manage_settings, view_all_contracts,
      //               manage_contracts, invite_users, manage_roles, view_analytics
      const permissions = createPermissions.split(',').map(p => p.trim()) as any[];

      const request: CreateOrgCustomRoleRequestContent = {
        orgId: createOrgId.trim(),
        name: createRoleName.trim(),
        description: createRoleDescription.trim() || undefined,
        permissions
      };
      const result = await createOrgCustomRoleApiv2(request);
      setCreateResponse(result);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleListRoles = async () => {
    if (!listOrgId.trim()) {
      setListError('Please enter an organization ID');
      return;
    }

    setListLoading(true);
    setListError(null);

    try {
      const result = await listOrgCustomRolesApiv2({ orgId: listOrgId.trim() });
      setListResponse(result);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setListLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!updateOrgId.trim() || !updateRoleId.trim()) {
      setUpdateError('Please enter both organization ID and role ID');
      return;
    }

    setUpdateLoading(true);
    setUpdateError(null);

    try {
      const request: UpdateOrgCustomRoleRequestContent = {
        orgId: updateOrgId.trim(),
        customRoleId: updateRoleId.trim(),
        name: updateRoleName.trim() || undefined,
        permissions: updatePermissions.trim() ? updatePermissions.split(',').map(p => p.trim()) as any[] : undefined
      };
      const result = await updateOrgCustomRoleApiv2(request);
      setUpdateResponse(result);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteOrgId.trim() || !deleteRoleId.trim()) {
      setDeleteError('Please enter both organization ID and role ID');
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      const result = await deleteOrgCustomRoleApi({
        orgId: deleteOrgId.trim(),
        customRoleId: deleteRoleId.trim()
      });
      setDeleteResponse(result);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#fff' }}>
        Custom Role Management APIs
      </Typography>

      {/* Create Role */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Create Custom Role</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={createOrgId}
              onChange={(e) => setCreateOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Role Name"
              value={createRoleName}
              onChange={(e) => setCreateRoleName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Description (optional)"
              value={createRoleDescription}
              onChange={(e) => setCreateRoleDescription(e.target.value)}
              fullWidth
            />
            <TextField
              label="Permissions (comma-separated)"
              value={createPermissions}
              onChange={(e) => setCreatePermissions(e.target.value)}
              fullWidth
              placeholder="e.g., manage_members, invite_users"
              helperText="Valid: manage_members, manage_billing, manage_settings, view_all_contracts, manage_contracts, invite_users, manage_roles, view_analytics"
            />
            <Button
              variant="contained"
              onClick={handleCreateRole}
              disabled={createLoading}
            >
              {createLoading ? <CircularProgress size={24} /> : 'Create Role'}
            </Button>
            {createResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(createResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {createError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{createError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* List Roles */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">List Custom Roles</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={listOrgId}
              onChange={(e) => setListOrgId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleListRoles}
              disabled={listLoading}
            >
              {listLoading ? <CircularProgress size={24} /> : 'List Roles'}
            </Button>
            {listResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                  Custom Roles ({Object.keys(listResponse.roles || {}).length}):
                </Typography>
                {listResponse.roles && Object.keys(listResponse.roles).length > 0 ? (
                  <List>
                    {Object.entries(listResponse.roles).map(([roleId, role], index) => (
                      <React.Fragment key={roleId}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {role.name}
                                <Chip label={`${role.permissions?.length || 0} permissions`} size="small" />
                                {role.memberCount && role.memberCount > 0 && (
                                  <Chip label={`${role.memberCount} members`} size="small" color="primary" />
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                Role ID: {role.customRoleId}
                                <br />
                                {role.description && (
                                  <>
                                    Description: {role.description}
                                    <br />
                                  </>
                                )}
                                Permissions: {role.permissions?.join(', ') || 'None'}
                              </>
                            }
                          />
                        </ListItem>
                        {index < Object.keys(listResponse.roles).length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography>No custom roles found</Typography>
                )}
              </Paper>
            )}
            {listError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{listError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Update Role */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Update Custom Role</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={updateOrgId}
              onChange={(e) => setUpdateOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Role ID"
              value={updateRoleId}
              onChange={(e) => setUpdateRoleId(e.target.value)}
              fullWidth
            />
            <TextField
              label="New Role Name (optional)"
              value={updateRoleName}
              onChange={(e) => setUpdateRoleName(e.target.value)}
              fullWidth
            />
            <TextField
              label="New Permissions (comma-separated, optional)"
              value={updatePermissions}
              onChange={(e) => setUpdatePermissions(e.target.value)}
              fullWidth
              placeholder="e.g., manage_members, invite_users"
              helperText="Valid: manage_members, manage_billing, manage_settings, view_all_contracts, manage_contracts, invite_users, manage_roles, view_analytics"
            />
            <Button
              variant="contained"
              onClick={handleUpdateRole}
              disabled={updateLoading}
            >
              {updateLoading ? <CircularProgress size={24} /> : 'Update Role'}
            </Button>
            {updateResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(updateResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {updateError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{updateError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Delete Role */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Delete Custom Role</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={deleteOrgId}
              onChange={(e) => setDeleteOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Role ID"
              value={deleteRoleId}
              onChange={(e) => setDeleteRoleId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleDeleteRole}
              disabled={deleteLoading}
              color="error"
            >
              {deleteLoading ? <CircularProgress size={24} /> : 'Delete Role'}
            </Button>
            {deleteResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(deleteResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {deleteError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{deleteError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TestRoles;
